import { IonIcon } from '@ionic/react';
import { chevronDownOutline, swapVerticalOutline } from 'ionicons/icons';
import type { ComponentProps, ReactNode } from 'react';
import { cn } from '../../lib/utils';
import './SelectField.css';

type IconType = ComponentProps<typeof IonIcon>['icon'];

export interface SelectFieldProps {
  /** Small uppercase caption above the value (e.g. "TEMPORADA"). */
  label?: string;
  /** The current selection shown in bold. */
  value: ReactNode;
  /** Optional leading icon, tinted gold to match the brand. */
  icon?: IconType;
  /** Trailing affordance: up/down arrows (default) or a single chevron. */
  chevron?: 'updown' | 'down';
  onClick?: () => void;
  disabled?: boolean;
  fullWidth?: boolean;
  className?: string;
  testId?: string;
  'aria-label'?: string;
}

/**
 * Shared styled selector trigger used across the app (season/competition
 * pickers, the market "En venta por" filter, …). Presentational only: the
 * caller wires up the actual picking (an IonSelect, action sheet, modal, …).
 */
export const SelectField = ({
  label,
  value,
  icon,
  chevron = 'down',
  onClick,
  disabled = false,
  fullWidth = true,
  className,
  testId,
  'aria-label': ariaLabel,
}: SelectFieldProps) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    data-testid={testId}
    aria-label={ariaLabel}
    className={cn(
      'fc-select-field flex min-h-[54px] min-w-0 items-center gap-3 bg-white text-left shadow-[0_1px_2px_rgba(20,30,60,0.04)]',
      'transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--fc-navy)]',
      'disabled:cursor-not-allowed disabled:opacity-60',
      fullWidth ? 'w-full' : 'w-fit',
      className
    )}
  >
    {icon && (
      <IonIcon
        icon={icon}
        aria-hidden="true"
        className="shrink-0 text-xl text-[#c19a3c]"
      />
    )}
    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
      {label && (
        <span className="text-[10px] font-extrabold uppercase leading-none tracking-wide text-[var(--fc-ink-3)]">
          {label}
        </span>
      )}
      <span className="truncate font-heading text-[15px] font-bold leading-tight text-[var(--fc-ink)]">
        {value}
      </span>
    </span>
    <IonIcon
      icon={chevron === 'down' ? chevronDownOutline : swapVerticalOutline}
      aria-hidden="true"
      className="shrink-0 text-base text-[var(--fc-ink-3)]"
    />
  </button>
);
